"use client";

import { createContext, ReactNode, useContext } from "react";

import { QueryObserverResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";

import { getMyUser } from "@/app/lib/actions/user";
import type { Organization, OrgMembership, User } from "@/app/lib/definitions";
import type { Permission, PermissionProfile } from "@/app/lib/definitions/permission";

interface UserContextType {
  currentMembership: OrgMembership | null | undefined;
  currentOrganization: Organization | null | undefined;
  getCachedUser: () => User | null | undefined;
  hasAllPermissions: (requiredPermissions: string | string[]) => boolean;
  hasAnyPermission: (requiredPermissions: string | string[]) => boolean;
  isLoading: boolean;
  numOrganizations: number;
  permissions: Permission["api_name"][];
  permissionProfile: PermissionProfile | null | undefined;
  refetchUser: () => Promise<QueryObserverResult<User, Error>>;
  setUser: (user: User | null | undefined) => void;
  user: User | null | undefined;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const hasAllPermissions = (permissions: string[], requiredPermissions: string | string[]) => {
  if (!permissions) {
    return false;
  }
  if (Array.isArray(requiredPermissions)) {
    return requiredPermissions.every((permission) => permissions.includes(permission));
  }
  return permissions.includes(requiredPermissions);
};

const hasAnyPermission = (permissions: string[], requiredPermissions: string | string[]) => {
  if (!permissions) {
    return false;
  }
  if (Array.isArray(requiredPermissions)) {
    return requiredPermissions.some((permission) => permissions.includes(permission));
  }
  return permissions.includes(requiredPermissions);
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const pathname: string = usePathname();
  const queryClient = useQueryClient();

  const protectedRoutes: string[] = ["/app", "/onboard"];
  const isProtectedRoute: boolean = protectedRoutes.some((route: string): boolean =>
    pathname.startsWith(route),
  );

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery({
    enabled: isProtectedRoute,
    queryKey: ["my-user"],
    queryFn: () => getMyUser(),
    staleTime: 30 * 60 * 1000,
  });

  // This is a way to try and get the cached user within an unprotected route
  // The use-case is that a user is authenticated but has navigated to an unprotected route and we
  // want to show them a button redirecting them back to the app.
  // During development this might be cleared by Next.js hot reloading which makes this hard to test.
  const getCachedUser = (): User | null | undefined => {
    return queryClient.getQueryData(["my-user"]);
  };

  const currentMembership = user?.current_membership;

  // /users/me/ serializes current_membership with organization expanded, so a string
  // ref is never expected here; the narrowing guards against an unexpanded payload
  const membershipOrganization = user?.current_membership?.organization;
  const currentOrganization =
    typeof membershipOrganization === "string" ? undefined : membershipOrganization;
  const numOrganizations = user?.org_memberships?.length ?? 0;
  const permissionProfile = user?.permission_profile;
  const permissions = user?.permissions ?? [];

  const refetchUser = () => refetch();

  const setUser = (newUser: User | null | undefined) => {
    queryClient.setQueryData(["my-user"], newUser);
  };

  return (
    <UserContext.Provider
      value={{
        currentMembership,
        currentOrganization,
        getCachedUser,
        hasAllPermissions: (requiredPermissions: string | string[]) =>
          hasAllPermissions(permissions, requiredPermissions),
        hasAnyPermission: (requiredPermissions: string | string[]) =>
          hasAnyPermission(permissions, requiredPermissions),
        isLoading,
        permissionProfile,
        permissions,
        numOrganizations,
        refetchUser,
        setUser,
        user,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
